import { type ComponentProps, type ReactNode } from "react";

import { Link } from "@/i18n/navigation";

type LinkHref = ComponentProps<typeof Link>["href"];

/**
 * Inline affordance wrapped in a pair of diagonally-opposed corner
 * marks (⌝ top-right, ⌞ bottom-left) so it reads as if it sits inside
 * an implied rectangle. Used for the editorial "view the gallery /
 * see the offering / view all projects / directions" style links —
 * NOT for the rounded pill CTA buttons, which already have their own
 * visual frame.
 *
 * Renders one of four element types based on props:
 * - `href` omitted, no `onClick`        → <span>  (decorative, parent owns click)
 * - `onClick` set                       → <button> (in-page action)
 * - `href` + `external` true            → <a target="_blank">
 * - `href` + `external` false (default) → next-intl locale-aware <Link>
 */
type FramedLinkProps = {
  children: ReactNode;
  className?: string;
} & (
  | {
      href?: never;
      external?: never;
      onClick?: never;
      scroll?: never;
      type?: never;
      disabled?: never;
    }
  | {
      onClick: () => void;
      type?: "button";
      disabled?: boolean;
      href?: never;
      external?: never;
      scroll?: never;
    }
  | {
      href: string;
      external: true;
      onClick?: never;
      scroll?: never;
      type?: never;
      disabled?: never;
    }
  | {
      href: LinkHref;
      external?: false;
      onClick?: never;
      /** Forwarded to next-intl Link; pass `false` to keep the viewport
       *  in place across navigation (paginated/filtered listings). */
      scroll?: boolean;
      type?: never;
      disabled?: never;
    }
);

export function FramedLink(props: FramedLinkProps) {
  const { children, className = "" } = props;
  // 3px over 5px, not 4 and 4: the mono face's words sit low in their line
  // box, so the label rode under the middle of the frame the corners draw.
  const wrapperClass = `group relative inline-block px-3 pt-0.75 pb-1.25 focus-visible:outline-none focus-visible:no-underline ${className}`;
  const cornerClass =
    "absolute leading-none text-fg-subtle transition-colors group-focus-visible:text-fg group-hover:text-fg-muted";
  const corners = (
    <>
      <span aria-hidden className={`${cornerClass} top-0 right-0`}>
        ⌝
      </span>
      <span aria-hidden className={`${cornerClass} bottom-0 left-0`}>
        ⌞
      </span>
    </>
  );

  if ("onClick" in props && props.onClick) {
    return (
      <button
        type={props.type ?? "button"}
        onClick={props.onClick}
        disabled={props.disabled}
        className={`${wrapperClass} cursor-pointer disabled:cursor-not-allowed`}
      >
        {corners}
        {children}
      </button>
    );
  }
  if (!("href" in props) || props.href === undefined) {
    return (
      <span className={wrapperClass}>
        {corners}
        {children}
      </span>
    );
  }
  if (props.external) {
    return (
      <a
        href={props.href}
        target="_blank"
        rel="noreferrer"
        className={wrapperClass}
      >
        {corners}
        {children}
      </a>
    );
  }
  return (
    <Link href={props.href} className={wrapperClass} scroll={props.scroll}>
      {corners}
      {children}
    </Link>
  );
}
